/**
 * Client-side background removal using Canvas API
 * Uses tolerance-based flood-fill algorithm to remove background colors
 */

export async function removeBackground(
    imageDataUrl: string,
    tolerance: number = 30
): Promise<string> {
    return new Promise((resolve, reject) => {
        const img = new Image();

        img.onload = () => {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');

            if (!ctx) {
                reject(new Error('Could not get canvas context'));
                return;
            }

            canvas.width = img.width;
            canvas.height = img.height;
            ctx.drawImage(img, 0, 0);

            const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
            const data = imageData.data;

            // Sample corner pixels to determine background color
            const corners = [
                { x: 0, y: 0 },
                { x: canvas.width - 1, y: 0 },
                { x: 0, y: canvas.height - 1 },
                { x: canvas.width - 1, y: canvas.height - 1 },
            ];

            const bgColors: number[][] = corners.map(({ x, y }) => {
                const index = (y * canvas.width + x) * 4;
                return [data[index], data[index + 1], data[index + 2]];
            });

            // Process each pixel
            for (let i = 0; i < data.length; i += 4) {
                const r = data[i];
                const g = data[i + 1];
                const b = data[i + 2];

                // Check if pixel color is close to any background color
                const isBackground = bgColors.some((bgColor) => {
                    const distance = Math.sqrt(
                        Math.pow(r - bgColor[0], 2) +
                        Math.pow(g - bgColor[1], 2) +
                        Math.pow(b - bgColor[2], 2)
                    );
                    return distance <= tolerance;
                });

                // Set alpha to 0 if background
                if (isBackground) {
                    data[i + 3] = 0;
                }
            }

            // Apply edge smoothing for anti-aliasing
            smoothEdges(imageData);

            ctx.putImageData(imageData, 0, 0);
            resolve(canvas.toDataURL('image/png'));
        };

        img.onerror = () => reject(new Error('Failed to load image'));
        img.src = imageDataUrl;
    });
}

/**
 * Smooth edges for better anti-aliasing
 */
function smoothEdges(imageData: ImageData): void {
    const data = imageData.data;
    const width = imageData.width;
    const height = imageData.height;

    const tempData = new Uint8ClampedArray(data);

    for (let y = 1; y < height - 1; y++) {
        for (let x = 1; x < width - 1; x++) {
            const index = (y * width + x) * 4;
            const alpha = data[index + 3];

            // Only process edge pixels (partially transparent)
            if (alpha > 0 && alpha < 255) {
                // Sample surrounding pixels
                const neighbors = [
                    (y - 1) * width + (x - 1),
                    (y - 1) * width + x,
                    (y - 1) * width + (x + 1),
                    y * width + (x - 1),
                    y * width + (x + 1),
                    (y + 1) * width + (x - 1),
                    (y + 1) * width + x,
                    (y + 1) * width + (x + 1),
                ];

                let alphaSum = alpha;
                let count = 1;

                neighbors.forEach((neighborIndex) => {
                    const nAlpha = tempData[neighborIndex * 4 + 3];
                    alphaSum += nAlpha;
                    count++;
                });

                // Average alpha for smooth transition
                data[index + 3] = Math.floor(alphaSum / count);
            }
        }
    }
}
